import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { OrigamiHttpResponse, OrigamiResponse, ServiceBase } from './base/service.base';
import { getServerConfig } from '../server-config';
import { TemplateType } from '../constants/template-types';
import { TimeZoneInfo, TmfPublishing, Topic, TopicField } from '../utils/topic-utils';
import { RoleLevel } from '../constants/role-levels';
import { ErrorCode } from '../constants/error-codes';
import { HttpHeaders } from '@angular/common/http';
import { UserStatus } from '@app/origami-api/constants/user-statuses';
import { Action } from '@app/origami-api/constants/actions';
import { BuildProps } from '../build-props';
import { WorkflowType } from '@app/origami-api/constants/workflow-type';
import { TeamDetailsGroup, TeamGroupType } from '@app/origami-api/services/teams.service';

export namespace UserServiceOptions {
  export interface EditUserOptions {
    siteId: number;
    user: User;
    forceDelete?: boolean;
    sendInvitation?: boolean;
    removePreviousActions?: boolean;
  }

  export interface GetUserDetailsOptions {
    siteId: number;
    userName?: string;
    emailAddress?: string;
    isInvitation?: boolean;
    searchInIam?: boolean;
  }

  export interface SendMailOptions {
    TemplateType?: number;
    QueryEntityId?: number;
    RecipientGroups?: string[];
    RecipientUsers?: string[];
    CarbonCopyGroups?: string[];
    CarbonCopyUsers?: string[];
    Subject?: string;
    Body?: string;
    Attachments?: number[];
    AttachmentsPaths?: any[];
    AttachmentsCifsPaths?: {
      CifsFilePath: string;
      CifsFileName: string;
    }[];
    AttachmentsAsLinks?: boolean;
    LinkConfig?: any;
    ProducePdfFile?: boolean;
    MissingDocuments?: Topic[];
    QueryTicketParams?: {
      QueryTicketId?: number;
      RelatedEntityId?: number;
      QueryEntityId?: number;
      RelatedEntityType?: any;
      Issues?: any;
      QueryType?: number;
      QueryCreatedBy?: number;
      WorkflowType?: WorkflowType;
    };
  }

  export interface SendResetPasswordEmailOptions {
    userName: string;
    domainName?: string;
  }

  export interface ChangePassswordOptions {
    newPassword: string;
    password: string;
  }

  export interface ChangeUserPasswordByLinkOptions {
    resetPassCode: string;
    password?: string;
    passwordResetAnswers?: any[];
  }

  export interface ChangeUserPasswordOptions {
    password?: string;
    newPassword?: string;
    userId?: number;
  }

  export interface GetResetPasswordQuestionsAnswers {
    domainName: string;
    userName: string;
  }

  export interface User {
    UserId?: number;
    EmailAddress?: string;
    FirstName?: string;
    LastName?: string;
    Address?: string;
    City?: string;
    State?: string;
    ZipCode?: string;
    Country?: string;
    MobileNo?: string;
    PhoneNo?: string;
    UserName?: string;
    GlobalUserTopic?: object;
    UserTopic?: object;
    AclGroups?: UserServiceModel.AclGroup[];
    Actions?: UserServiceModel.Action[];
  }

  export interface CreateUser {
    password: string;
    sendInvitation: boolean;
    silentInvite: boolean;
    siteId: number;
    users: {
      FirstName: string;
      MiddleName: string;
      LastName: string;
      Title: string;
      Prefix: string;
      Suffix: string;
      Actions: UserServiceModel.Action[];
      MobileNo: string;
      PhoneNo: string;
      Address: string;
      City: string;
      State: string;
      ZipCode: string;
      Country: string;
      UserName: string;
      EmailAddress: string;
      AllowMultipleSessions: boolean;
      Groups: any[];
      OrganizationName: string;
      UserTopic: {
        TopicFields: TopicField[];
      };
      GlobalUserTopic: {
        TopicFields: TopicField[];
      };
      AclGroups: UserServiceModel.AclGroup[];
    }[];
  }

  export interface ResetQuestion {
    QuestionId: number;
    Question: string;
    Answer: string;
  }

  export interface RegisterUser {
    siteId: number;
    user: User;
    password: string;
    confirmPassword: string;
    pin: string;
    reqExtraInfo?: boolean;
    secretQuestions: ResetQuestion[];
  }

  export interface GetUsersByView {
    // TODO: This interface may have others properties
    siteId?: number;
    filter?: UserStatus | Action;
    limit?: number;
    start?: number;
    orderByClause?: string;
    searchClause?: string;
    viewTypeId: number;
  }

  export interface DeleteSiteUsers {
    siteId: number;
    userNames: string;
    forceDelete?: boolean;
  }

  export interface InviteUsers {
    siteId: number;
    userNames: string;
  }

  export interface SetUserProfilePicture {
    userProfilePicture: {
      FileToken: string;
      Params: {
        Style: string;
        Width: string;
        Height: string;
      }[];
    };
  }

  export interface SetBrowserUserInfo {
    timeZoneKey: string;
  }

  export interface GetUsersBy {
    siteId?: number;
    orderByClause?: string;
    start?: number;
    limit?: number;
    invSiteId?: number;
    tmfPublishing?: TmfPublishing;
    getExtendedInfo?: boolean;
  }

  export interface GetUsersBySite extends GetUsersBy {
    searchClause?: string;
    minRoleLevel?: number;
    maxRoleLevel?: number;
    userNames?: string[];
    userIds?: number[];
    actionFilter?: number;
    showInactive?: boolean;
    organizationId?: number;
    registeredDuringDays?: number;
    returnInvSiteContacts?: boolean;
    exportParam?: {
      ExportFormat?: string;
    };
    FieldListing?: string;
    filters?: {
      Groups?: number[];
      Sites?: number[];
      Teams?: number[];
      Organizations?: number[];
      ClientOrganizations?: number[];
    };
  }

  export interface GetUsersByGroup extends GetUsersBy {
    groupName?: string | number;
    recursively?: boolean;
    searchClause?: string;
  }

  export type GetUsersBySiteForTaskAssignment = GetUsersBy;

  export interface GetContactSupport {
    contactSupportParams: {
      Body: string;
      Subject: string;
      UserEmailAddress: string;
    };
    siteId: number;
  }

  export interface BulkUserInviteOptions {
    actionIds: string[];
    groupNames: string;
    roleLevel: number;
    siteIds: number[];
    userNames: string;
  }

  export interface GetUsersResult {
    UserList: UserServiceModel.User[];
    TotalRowCount: number;
  }

  export interface GetPossibleInvestigativeSiteMembers {
    contactType: number;
    orderByClause?: string;
    siteId: number;
    searchClause?: string;
  }

  export interface VerifyUser {
    domainId: number;
    siteId: number;
    userName: string;
    PIN: number | string;
  }

  export interface GetEmailRecipients {
    siteId: number;
    groupIds: string[];
  }

  export interface GetDocumentOwnerUsers {
    siteId: number;
    topicId?: number;
    reviewId?: number;
    start?: number;
    limit?: number;
    orderByClause?: string;
    includeMainOwner?: boolean;
    searchClause?: string;
  }
}

export namespace UserServiceModel {
  export interface AdditionalInfo {
    InvestigativeSiteId: number;
    IsUserContact: boolean;
    IsUserExternalAuditor: boolean;
    IsSponsor?: boolean;
    IsUserRegistered: boolean;
    IsUserSaRoleSetter: boolean;
    IsUserSuperAdmin: boolean;
    Modules: { ModuleType: number }[];
    PasswordResetAnswersCount: number;
    PasswordExpirationDaysLeft: number;
    IsUserMyTiCRA: boolean;
    IsUserAuditorsManager?: boolean;
    IsUserDocumentManager?: boolean;
    IsUserQueryManager: boolean;
    IsUserGeneralQueryResponder?: boolean;
    IsUserLookup?: boolean;
    InvestigativeSiteIds?: number[];
  }

  export interface AclGroup {
    AccessExpirationDate: string;
    GroupId?: number;
    GroupName: string;
  }

  export interface Action {
    ActionId: number;
    ActionName: string;
    DisplayName?: string;
    Groups?: { ActionId: number; GroupId: number; GroupName: string }[];
    UserActionExists?: boolean;
    Modules?: {
      Default: boolean;
      ModuleName: string;
      Name: string;
      SortOrder: number;
    }[];
  }

  export interface IUserDetails {
    IsLocked: boolean;
    StatusId: number;
    UserId: number;
    EmailAddress: string;
    FirstName: string;
    MiddleName: string;
    LastName: string;
    Title: string;
    Prefix: string;
    Suffix: string;
    MobileNo: string;
    PhoneNo: string;
    Address: string;
    City: string;
    State: string;
    ZipCode: string;
    Country: string;
    MFARegistered: boolean | null;
  }

  export interface UserTeam {
    Name: string;
    TeamId: number;
    Type: TeamGroupType;
    TeamGroups: TeamDetailsGroup[];
  }

  export interface UserGroupData {
    DisplayName: string;
    GroupId: number;
    GroupName: string;
  }

  export interface User {
    GlobalUserTopic?: Topic;
    UserTopic?: Topic;
    AclGroups: AclGroup[];
    Actions: Action[];
    AdditionalInfo?: AdditionalInfo;
    Address: string;
    AllowMultipleSessions: boolean;
    AssociatedRoleLevel: number;
    AssociatedRoomId: number;
    AssociatedRoomName: string;
    City: string;
    ClinicalTrialExperience: number;
    ContactId: number;
    ContactStatusId: number;
    ContactStatus?: number;
    ContactType: string | number;
    ContactTypeId?: number;
    Country: string;
    CreatedBy: number;
    CreatedDate: string;
    DomainId: number;
    EmailAddress: string;
    FirstName: string;
    MiddleName: string;
    FullName: string;
    GroupActionsStr: string;
    ZipCode: string;
    InvitedOn: any;
    IsEmailAutogenerated: boolean;
    IsLocked: boolean;
    IsSsoEnabled: boolean;
    IsUserMainContact: boolean;
    IsUserMainDocumentOwner: boolean;
    IsUserSitesMember: boolean;
    IsUserAuditor: boolean;
    IsUserAuditorsManager: boolean;
    IsUserAuditResponseUser: boolean;
    IsRegistered: boolean;
    LastName: string;
    LastUpdated: string;
    LastUpdatedBy: number;
    Locale: string;
    MFARegistered: boolean | null;
    MaxUserRoleLevelAcrossRooms: number;
    MobileNo: string;
    OrganizationName: string;
    PhoneNo: string;
    RegisteredIAMUser: boolean;
    RoleLevel: RoleLevel;
    State: string;
    StatusId: number;
    UserId: number;
    id?: number;
    UserActionsStr: string;
    UserGroupsStr: string;
    UserName: string;
    UserProfilePictureLastUpdatedDate?: number;
    Title: string;
    Prefix: string;
    Suffix: string;
    IsRoleChange?: boolean;
    ServerTimeZone: TimeZoneInfo;
    UserTimeZone: TimeZoneInfo;
    IAMUserId?: number;
    IAMUserStatus?: number;
    TopicId?: number;
    Teams?: UserTeam[];
    UserGroupsData?: UserGroupData[];
    HasAvatar?: boolean;
  }

  export interface SendMailResult {
    ProducePdfFileToken: string;
    Recepients: string;
    RecepientsCC: string;
    Sender: string;
    UsersErrorCodes: {
      ObjectName: string;
      ObjectType: string;
      ErrorCode: number;
      ErrorMessage?: string;
      ErrorRecords: {
        ErrorCode: number;
        ObjectName: string;
        ObjectType: string;
      }[];
    }[];
  }

  export interface ChangeUserPasswordResult {
    ErrorCode: number;
    Link: string;
  }

  export interface AdminMessageResult {
    Message: string;
    MessageType?: number;
  }
}

@Injectable()
export class UserService extends ServiceBase {
  serviceUrl = 'UserService.svc';

  activateUsers(options: {
    userNames: string[];
    restoreAccess: boolean;
    reason?: string;
  }): Observable<OrigamiResponse<any[]>> {
    return this.origamiPost('/ActivateUsers', options, null, null, true);
  }

  deactivateUsers(
    options: { userNames: string[]; forceDelete: boolean },
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<any[]>> {
    return this.origamiPost('/DeactivateUsers', options, null, silentErrorCodes, true);
  }

  unlockUser(userName: string): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/UnlockUser', { userName }, null, null, true);
  }

  editUser(
    options: UserServiceOptions.EditUserOptions,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<any[]>> {
    return this.origamiPost('/EditUser', options, null, silentErrorCodes, true);
  }

  getUserDetails(
    options: UserServiceOptions.GetUserDetailsOptions,
    silentErrorCodes?: ErrorCode[],
    forceRequest?,
  ): Observable<OrigamiResponse<UserServiceModel.User>> {
    options = options || ({} as UserServiceOptions.GetUserDetailsOptions);
    return this.origamiPost('/GetUserDetails', options, null, silentErrorCodes, forceRequest);
  }

  getUsersBySite(
    body?: UserServiceOptions.GetUsersBySite,
    options?,
    silentErrorCodes?: ErrorCode[],
    forceRequest?,
  ): Observable<OrigamiResponse<UserServiceOptions.GetUsersResult>> {
    return this.origamiPost('/GetUsersBySite', body, options, silentErrorCodes, forceRequest);
  }

  getUsersByView(
    options: UserServiceOptions.GetUsersByView,
    ...params
  ): Observable<OrigamiResponse<UserServiceOptions.GetUsersResult>> {
    return this.origamiPost('/GetUsersByView', options, ...params);
  }

  getUsersStatuses(angularHttpOptions?, silentErrorCodes?, forceRequest?): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/GetUsersStatuses', {}, angularHttpOptions, silentErrorCodes, forceRequest);
  }

  sendMail(
    body: { siteId: number; sendMailParams: UserServiceOptions.SendMailOptions },
    errorCodes?: number[],
  ): Observable<OrigamiResponse<UserServiceModel.SendMailResult>> {
    body.sendMailParams.TemplateType = body.sendMailParams.TemplateType || TemplateType.UserEmail;
    return this.origamiPost('/SendMail', body, null, errorCodes, true);
  }

  sendMails(
    body: { siteId: number; sendMailParams: UserServiceOptions.SendMailOptions[] },
    errorCodes?: number[],
  ): Observable<OrigamiResponse<number>> {
    body.sendMailParams.map((sendMailParam) => {
      sendMailParam.TemplateType = sendMailParam.TemplateType || TemplateType.UserEmail;
      return sendMailParam;
    });
    return this.origamiPost('/SendMails', body, null, errorCodes, true);
  }

  sendResetPasswordEmail(options: UserServiceOptions.SendResetPasswordEmailOptions): Observable<OrigamiHttpResponse> {
    options.domainName = options.domainName || getServerConfig().domain;

    return this.origamiPost('/SendResetPasswordEmail', options, null, null, true);
  }

  changeUserPasswordByLink(
    options: UserServiceOptions.ChangeUserPasswordByLinkOptions,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/ChangeUserPasswordByLink', options, null, silentErrorCodes, true);
  }

  changeUserPassword(
    options: UserServiceOptions.ChangeUserPasswordOptions,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/ChangeUserPassword', options, null, silentErrorCodes, true);
  }

  changeIAMUserPassword(
    options: UserServiceOptions.ChangeUserPasswordOptions,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiHttpResponse> {
    const url = BuildProps.origin + 'api/portal/users/update/password/confirm';

    return this.basePost(url, options, null, silentErrorCodes, true);
  }

  getResetPasswordQuestionsAnswers(
    options: UserServiceOptions.GetResetPasswordQuestionsAnswers,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/GetResetPasswordQuestionsAnswers', options, null, silentErrorCodes, true);
  }

  bulkUserInvite(options: UserServiceOptions.BulkUserInviteOptions): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/BulkUserInvite', options, null, null, true);
  }

  bulkUpload(options: any, silentErrorCodes: ErrorCode[]): Observable<OrigamiHttpResponse> {
    return this.origamiPost(
      '/ImportUsers',
      options,
      {
        headers: new HttpHeaders(),
      },
      silentErrorCodes,
      true,
    );
  }

  registerUser(options: UserServiceOptions.RegisterUser): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/RegisterUser', options, null, null, true);
  }

  setResetPasswordAnswers(options: any): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/SetResetPasswordAnswers', options, null, null, true);
  }

  createUser(options: any): Observable<OrigamiHttpResponse> {
    const silentErrorCodes = [ErrorCode.CREATE_USER_ERROR];

    return this.origamiPost('/CreateUser', options, null, silentErrorCodes, true);
  }

  resetPassword(options: any): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/ResetPassword', options, null, null, true);
  }

  deleteSiteUsers(options: UserServiceOptions.DeleteSiteUsers, silentErrorCodes?): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/DeleteSiteUsers', options, null, silentErrorCodes, true);
  }

  inviteUsers(options: UserServiceOptions.InviteUsers): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/InviteUsers', options, null, null, true);
  }

  setUserProfilePicture(options: UserServiceOptions.SetUserProfilePicture): Observable<OrigamiResponse<boolean>> {
    return this.origamiPost('/SetUserProfilePicture', options, null, null, true);
  }

  setBrowserUserInfo(options: UserServiceOptions.SetBrowserUserInfo): Observable<OrigamiResponse<boolean>> {
    return this.origamiPost('/SetBrowserUserInfo', options, null, null, true);
  }

  deleteUserProfilePicture(): Observable<OrigamiResponse<boolean>> {
    return this.origamiPost('/DeleteUserProfilePicture', {}, null, null, true);
  }

  getAdminMessages(): Observable<OrigamiResponse<UserServiceModel.AdminMessageResult[]>> {
    return this.origamiPost('/GetAdminMessages', {}, null, null, true);
  }

  getUsersByGroup(
    body: UserServiceOptions.GetUsersByGroup,
    ...params
  ): Observable<OrigamiResponse<UserServiceOptions.GetUsersResult>> {
    return this.origamiPost('/GetUsersByGroup', body, ...params);
  }

  getUsersBySiteForTaskAssignment(
    body: UserServiceOptions.GetUsersBySiteForTaskAssignment,
    ...params
  ): Observable<OrigamiResponse<UserServiceOptions.GetUsersResult>> {
    return this.origamiPost('/GetUsersBySiteForTaskAssignment', body, ...params);
  }

  contactSupport(body: UserServiceOptions.GetContactSupport): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/ContactSupport', body, null, null, true);
  }

  getPossibleInvestigativeSiteMembers(body: UserServiceOptions.GetPossibleInvestigativeSiteMembers, ...params) {
    return this.origamiPost('/GetPossibleInvestigativeSiteMembers', body, ...params);
  }

  verifyUser(
    options: UserServiceOptions.VerifyUser,
    silentErrorCodes?: ErrorCode[],
    force = false,
  ): Observable<OrigamiResponse<boolean>> {
    return this.origamiPost('/VerifyUser', options, null, silentErrorCodes, force);
  }

  getEmailRecipients(
    body: UserServiceOptions.GetEmailRecipients,
    ...params
  ): Observable<OrigamiResponse<UserServiceModel.User[]>> {
    return this.origamiPost('/GetEmailRecipients', body, null, null, true);
  }

  getDocumentOwnerUsers(
    body: UserServiceOptions.GetDocumentOwnerUsers,
    ...param
  ): Observable<OrigamiResponse<UserServiceOptions.GetUsersResult>> {
    return this.origamiPost('/GetDocumentOwnerUsers', body, null, null, true);
  }

  geActiveUsersSessions(
    body: UserServiceOptions.GetDocumentOwnerUsers,
    ...param
  ): Observable<OrigamiResponse<UserServiceOptions.GetUsersResult>> {
    return this.origamiPost('/GetActiveUsersSessions', body, null, null, true);
  }
}
